import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { setAuthLostHandler, tokens } from "../api/client";
import { AuthApi } from "../api/services";

export type Role = "patient" | "responder" | "dispatcher" | "admin" | "cashier";
export type User = { id: string; name: string; email: string; phone?: string; role: Role; status: string; [k: string]: any };

type Ctx = {
  user: User | null;
  ready: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (body: any) => Promise<User>;
  acceptSession: (data: any) => Promise<User>;
  logout: () => Promise<void>;
  setUser: (u: User) => void;
};

const AuthContext = createContext<Ctx>(null as any);

export const ROLE_HOME: Record<Role, string> = {
  patient: "/patient", responder: "/responder", dispatcher: "/dispatch", admin: "/admin", cashier: "/cashier",
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    setAuthLostHandler(() => { setUser(null); qc.clear(); });
    (async () => {
      if (await tokens.load()) {
        try { setUser(await AuthApi.me()); } catch { /* invalid session, stays logged out */ }
      }
      setReady(true);
    })();
  }, [qc]);

  const accept = useCallback(async (data: any) => {
    await tokens.set(data.access_token, data.refresh_token);
    qc.clear();
    setUser(data.user);
    return data.user as User;
  }, [qc]);

  const login = useCallback(async (email: string, password: string) => accept(await AuthApi.login(email, password)), [accept]);
  const register = useCallback(async (body: any) => accept(await AuthApi.register(body)), [accept]);
  const logout = useCallback(async () => {
    try { await AuthApi.logout(); } catch { /* ignore network failure on logout */ }
    await tokens.clear();
    qc.clear();
    setUser(null);
  }, [qc]);

  return <AuthContext.Provider value={{ user, ready, login, register, logout, setUser, acceptSession: accept }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
